import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Switch,
  IconButton,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CampaignIcon from '@mui/icons-material/Campaign';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import { comunicazioniApi } from '../../services/api';
import ResponsiveHeader from '../../components/ResponsiveHeader';
import ResponsiveFilters from '../../components/ResponsiveFilters';
import MobileCardList from '../../components/MobileCardList';
import { useMobile } from '../../hooks/useMobile';

const CommunicationList = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isMobile } = useMobile();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [search, setSearch] = useState('');
  const [deleteId, setDeleteId] = useState(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['communications', page, rowsPerPage, search],
    queryFn: () => comunicazioniApi.getAll({
      page: page + 1,
      limit: rowsPerPage,
      search: search || undefined
    }).then(r => r.data)
  });

  const toggleMutation = useMutation({
    mutationFn: (id) => comunicazioniApi.toggle(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['communications'] })
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => comunicazioniApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['communications'] });
      setDeleteId(null);
    }
  });

  const communications = data?.data || [];
  const total = data?.pagination?.total || 0;

  const getTargetSummary = (targets) => {
    if (!targets || targets.length === 0) return 'Tutte le fatture';
    const plantCount = new Set(targets.filter(t => t.plant_id).map(t => t.plant_id)).size;
    const meterOnly = targets.filter(t => t.meter_id).length;
    const parts = [];
    if (plantCount > 0) parts.push(`${plantCount} impianti`);
    if (meterOnly > 0) parts.push(`${meterOnly} contatori`);
    return parts.join(', ') || 'Tutte le fatture';
  };

  return (
    <Box>
      <ResponsiveHeader
        title="Comunicazioni"
        subtitle="Gestione delle comunicazioni commerciali e operative"
        primaryAction={{
          label: isMobile ? 'Nuova' : 'Nuova Comunicazione',
          icon: <AddIcon />,
          onClick: () => navigate('/crm/comunicazioni/nuova'),
        }}
      >
        <CampaignIcon sx={{ fontSize: 24, color: 'primary.main' }} />
      </ResponsiveHeader>

      <Card sx={{ mb: isMobile ? 1.5 : 2 }}>
        <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 } }}>
          <ResponsiveFilters
            searchValue={search}
            onSearchChange={(value) => {
              setSearch(value);
              setPage(0);
            }}
            searchPlaceholder="Cerca per titolo..."
          />
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }}>Errore nel caricamento: {error.message}</Alert>}

      {isMobile ? (
        <MobileCardList
          rows={communications}
          loading={isLoading}
          emptyMessage="Nessuna comunicazione trovata. Crea la prima!"
          pagination={{
            page,
            pageSize: rowsPerPage,
            total,
            onPageChange: setPage,
          }}
          renderCard={(comm) => (
            <Card variant="outlined">
              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="subtitle2" fontWeight={600} noWrap>
                      {comm.titolo}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      {getTargetSummary(comm.targets)}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1, flexWrap: 'wrap' }}>
                      <Chip
                        label={comm.attivo ? 'Attiva' : 'Disattiva'}
                        color={comm.attivo ? 'success' : 'default'}
                        size="small"
                      />
                      {comm.mostra_qrcode ? (
                        <Tooltip title="Box QR Code WhatsApp incluso">
                          <QrCode2Icon fontSize="small" sx={{ color: 'success.main' }} />
                        </Tooltip>
                      ) : null}
                      <Typography variant="caption" color="text.secondary">
                        {comm.created_at ? new Date(comm.created_at).toLocaleDateString('it-IT') : '-'}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5, flexShrink: 0 }}>
                    <Tooltip title={comm.attivo ? 'Clicca per disattivare' : 'Clicca per attivare'}>
                      <Switch
                        checked={!!comm.attivo}
                        onChange={() => toggleMutation.mutate(comm.id)}
                        color="primary"
                        size="small"
                      />
                    </Tooltip>
                    <Box>
                      <Tooltip title="Modifica">
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/crm/comunicazioni/${comm.id}/modifica`)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Elimina">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setDeleteId(comm.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          )}
        />
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Titolo</TableCell>
                <TableCell align="center">Stato</TableCell>
                <TableCell>Destinatari</TableCell>
                <TableCell>Data creazione</TableCell>
                <TableCell align="right">Azioni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    Caricamento...
                  </TableCell>
                </TableRow>
              ) : communications.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      Nessuna comunicazione trovata. Crea la prima!
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : communications.map((comm) => (
                <TableRow key={comm.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography fontWeight={600}>{comm.titolo}</Typography>
                      {comm.mostra_qrcode ? (
                        <Tooltip title="Box QR Code WhatsApp incluso">
                          <QrCode2Icon fontSize="small" sx={{ color: 'success.main' }} />
                        </Tooltip>
                      ) : null}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={comm.attivo ? 'Clicca per disattivare' : 'Clicca per attivare'}>
                      <Switch
                        checked={!!comm.attivo}
                        onChange={() => toggleMutation.mutate(comm.id)}
                        color="primary"
                      />
                    </Tooltip>
                    <Chip
                      label={comm.attivo ? 'Attiva' : 'Disattiva'}
                      color={comm.attivo ? 'success' : 'default'}
                      size="small"
                      sx={{ ml: 1 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {getTargetSummary(comm.targets)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {comm.created_at ? new Date(comm.created_at).toLocaleDateString('it-IT') : '-'}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Modifica">
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/crm/comunicazioni/${comm.id}/modifica`)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Elimina">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setDeleteId(comm.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50]}
            labelRowsPerPage="Righe per pagina"
          />
        </TableContainer>
      )}

      {/* Dialog conferma eliminazione */}
      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)} fullScreen={isMobile}>
        <DialogTitle>Conferma eliminazione</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Sei sicuro di voler eliminare questa comunicazione? L'azione non è reversibile.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>Annulla</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => deleteMutation.mutate(deleteId)}
          >
            Elimina
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CommunicationList;
